import { BOX_SHADOW_8_14_26 } from '@apps/card/constants/boxShadow';
import type { TemplateForAutofill } from '@apps/card/contexts/AutofillContext';
import { useAutofill } from '@apps/card/contexts/AutofillContext';
import { useCardPreviewWithPhotoContext } from '@apps/card/contexts/CardPreviewWithPhotoContext';
import { useScaleToContainer } from '@apps/card/hooks/useScaleToContainer';
import { CardDieCut } from '@apps/card/routes/CardCustomizer/components/CardDieCut';
import { Page } from '@apps/card/routes/CardCustomizer/components/Page';
import { cardDieCutToShapeConfig } from '@apps/card/utils';
import { buildCssVarStyle, imageVarIndices } from '@apps/card/utils/colorway';
import { autofillPhotos } from '@apps/card/utils/injectPhotosIntoTemplate';
import { animationTransition } from '@shared/utils/animationTransition';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { Box, Flex } from '@withjoy/joykit';
import { cloneDeep } from 'lodash-es';
import type { CSSProperties, ReactNode } from 'react';
import React from 'react';
import { FallbackCardBox } from './CardThumbnail.styles.internal';

type CardSize = Readonly<{ widthInches: number; heightInches: number }>;

const ECARD_SIZE: CardSize = { widthInches: 5, heightInches: 7 };

type CardThumbnailProps = {
  children?: ReactNode;
  template: TemplateForAutofill;
  /**
   * Which colorway to preview. Omit to show the template's persisted colorway
   * (or the first variant if none is persisted); pass a value to reflect an
   * interactive selection (e.g. gallery swatches).
   */
  selectedColorwayId?: string;
  /**
   * Static preview image shown for designs that have no `themeJSON` to render live:
   * digital-only / legacy ecards, and paper templates while the gallery is built from the
   * server-rendered summary (PRNT-5494). Opt-in from the caller, which picks the right image
   * — a paperAndDigital template also carries a `digitalDesignThumbnailUrl`, and inferring
   * the fallback from that would render the ecard image for a merely malformed paper template.
   */
  fallbackImageUrl?: string;
  /** The card the fallback image shows, so its box takes the card's shape; defaults to the 5x7 ecard. */
  fallbackCardSize?: CardSize;
  /**
   * Fit the fallback into the same box the live preview is scaled into (40px in on every side),
   * so swapping one for the other doesn't resize the card. Off, the frame keeps its narrower
   * mobile padding.
   */
  fallbackFitsLivePreview?: boolean;
  /** How the fallback image loads; `eager` also marks it high priority (above-the-fold tiles). Default `lazy`. */
  imageLoading?: 'eager' | 'lazy';
  /** Describes the design for search engines and screen readers. */
  altText?: string;
};

// Shared frame for both thumbnail variants (live <Page> and static fallback image)
// so the aspect ratio / padding / hover background can't drift between them.
type ThumbnailFrameProps = Readonly<{
  children: ReactNode;
  isMobile: boolean;
  /** 40px on every side at every width — the box the live preview is scaled into. */
  uniformPadding?: boolean;
  style?: CSSProperties;
}>;

const ThumbnailFrame = React.forwardRef<HTMLDivElement, ThumbnailFrameProps>(({ children, isMobile, uniformPadding = false, style }, ref) => (
  <Flex
    ref={ref}
    aspectRatio={isMobile ? '8 / 10' : '9 / 10'}
    overflow="hidden" // Needed for Safari
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    position="relative" // For badges
    paddingX={isMobile && !uniformPadding ? 4 : 8}
    paddingY={8}
    background="hsla(45, 12%, 93%, 1)"
    transition={animationTransition('background')}
    _groupHover={{
      background: 'hsla(45, 12%, 73%, 0.4)',
    }}
    // `contain: size` makes this frame size purely from its aspect-ratio and the grid track —
    // never from its contents. `useScaleToContainer` writes an explicit pixel size onto the
    // decorator below; without containment that size propagates up through this frame's
    // aspect-ratio and the tile's, back into the grid track, and the measure→write→relayout
    // loop latches into a wrong state under fast Safari zoom (the misalignment that only a
    // reload clears). Containment severs that loop so the measured frame stays stable.
    // `container-type: size` carries the same containment and makes the frame the query
    // container the fallback box (`FallbackCardBox`) fits itself into.
    // `containerType` is newer than this csstype, hence the cast.
    style={{ contain: 'size', ...({ containerType: 'size' } as CSSProperties), ...style }}
  >
    {children}
  </Flex>
));

export const CardThumbnail = (props: CardThumbnailProps) => {
  const { children, template, selectedColorwayId, fallbackImageUrl, fallbackCardSize, fallbackFitsLivePreview = false, imageLoading = 'lazy', altText = '' } = props;
  const isMobile = useIsMobileScreen();

  const [containerRef, decoratorRef, contentRef] = useScaleToContainer<HTMLDivElement>(40);

  const { autofill } = useAutofill();
  const { photos: previewPhotos } = useCardPreviewWithPhotoContext();

  // Skip templates with missing or malformed themeJSON early — before autofill and
  // photo-injection which also dereference themeJSON.card. A `fallbackImageUrl` (see the
  // prop) renders as a static preview instead of nothing.
  if (!template.themeJSON?.card?.front) {
    if (!fallbackImageUrl) return null;
    const { widthInches, heightInches } = fallbackCardSize ?? ECARD_SIZE;
    const fit = widthInches === heightInches ? 0.8 : 1;
    const isLandscape = widthInches > heightInches;
    const cardBoxStyle = {
      '--card-ratio': widthInches / heightInches,
      '--card-fit': fit,
      '--card-fallback-width': isLandscape ? `${fit * 100}%` : 'auto',
      '--card-fallback-height': isLandscape ? 'auto' : `${fit * 100}%`,
    } as CSSProperties;
    return (
      <ThumbnailFrame
        isMobile={isMobile}
        uniformPadding={fallbackFitsLivePreview}
      >
        <FallbackCardBox style={cardBoxStyle}>
          <img
            src={fallbackImageUrl}
            alt={altText}
            loading={imageLoading}
            // Lowercase so React passes it through as a plain attribute.
            {...(imageLoading === 'eager' ? ({ fetchpriority: 'high' } as Record<string, string>) : {})}
            style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </FallbackCardBox>
        {children}
      </ThumbnailFrame>
    );
  }

  // Resolve the colorway into inline CSS vars for preview. Derived per render
  // (cheap, pure) so the correct colorway shows as soon as the template loads —
  // no stale-state trap from initializing before async data arrives.
  const front = template.themeJSON.card.front;
  const options = front.colorways?.options;
  const colorwayId = selectedColorwayId ?? front.colorways?.selectedId ?? options?.[0]?.id;
  const selectedColorway = options?.find(o => o.id === colorwayId)?.values;
  const cssVarStyle = selectedColorway ? buildCssVarStyle(selectedColorway, imageVarIndices(front.layers ?? []), 'small') : {};
  const defaultCut = front.dimensions?.defaultCut ?? CardDieCut.rectangle;

  const clonedTemplate = cloneDeep(template);
  autofill(clonedTemplate);

  if (previewPhotos.length > 0 && !template.themeId?.startsWith('upload_your_')) {
    // User selected preview photos — inject them into image slots so the gallery shows
    // templates filled with their own photos. Skip upload_your_ templates as they have
    // their own dedicated upload affordance and should remain blank until the user uploads.
    autofillPhotos(
      clonedTemplate,
      previewPhotos.map(p => ({ url: p.url })),
    );
  }
  // The gallery is opt-in: with no selection the slots keep the template's stock photos.

  return (
    <ThumbnailFrame
      ref={containerRef}
      isMobile={isMobile}
      style={cssVarStyle}
    >
      <Box
        ref={decoratorRef}
        // The live preview is drawn from divs, not an image, so it takes the alt text as an
        // image role. Its layers become presentational, silencing their generic photo alts.
        role={altText ? 'img' : undefined}
        aria-label={altText || undefined}
        boxShadow={BOX_SHADOW_8_14_26}
        borderRadius={cardDieCutToShapeConfig(defaultCut).borderRadius}
        transition="border-radius .3s"
        // Absolutely positioned + centered so the explicit width/height `useScaleToContainer`
        // writes here stay out of flow and can't resize the frame (see the `contain: size`
        // note above — together they break the Safari fast-zoom feedback loop). The frame is
        // `position: relative`, so this centers within it exactly as the previous flex centering did.
        position="absolute"
        top="50%"
        left="50%"
        style={{ transform: 'translate(-50%, -50%)' }}
      >
        <Page
          ref={contentRef}
          page={clonedTemplate.themeJSON!.card.front}
          width={5}
          height={7}
          rendition="small"
          shape={defaultCut}
          forcePlaceholderQRCode
        />
      </Box>
      {children}
    </ThumbnailFrame>
  );
};
